import React, { useState } from "react";

import moment from "moment";

import { ChangeEventFamily, ChangeEventTimelineCounts, ChangeEventTimelineScope, ChangeEventTimelineView } from "models";

import { Form, InputSearch, RangePicker, Tooltip } from "components";

import { FAMILIES, SCOPES, activeFilterCount, familyClassName, toggleValue } from "pages/admin/verifications/utils/changeEventTimeline";

type ChangeEventFiltersProps = {
  counts: ChangeEventTimelineCounts;
  view: ChangeEventTimelineView;
  onChange: (patch: Partial<ChangeEventTimelineView>) => void;
  onReset: () => void;
  canReset: boolean;
};

type FilterChipProps = {
  label: string;
  count: number;
  pressed: boolean;
  family?: ChangeEventFamily;
  onToggle: (solo: boolean) => void;
};

/** A family with no rows at all is information, not a control — visible, but not worth a click. */
const FilterChip = ({ label, count, pressed, family, onToggle }: FilterChipProps) => (
  <Tooltip title={count ? `click to toggle · shift-click for only ${label}` : "no rows in this timeline"}>
    <button
      type="button"
      className={`ce-chip${family ? ` ${familyClassName(family)}` : ""}`}
      aria-pressed={pressed}
      disabled={!count}
      onClick={(clickEvent) => onToggle(clickEvent.shiftKey)}
    >
      {family && <span className="ce-swatch" />}
      {label}
      <i>{count}</i>
    </button>
  </Tooltip>
);

/**
 * One control row by default. Scope, family, baseline and dates live behind the disclosure, which is
 * forced open whenever a filter is non-default — a filter you cannot see is a trap.
 */
export const ChangeEventFilters = ({ counts, view, onChange, onReset, canReset }: ChangeEventFiltersProps) => {
  const [manuallyOpen, setManuallyOpen] = useState(false);

  const active = activeFilterCount(view);
  const open = manuallyOpen || active > 0;

  return (
    <div className="ce-controls">
      <div className="ce-row">
        <InputSearch
          className="ce-search"
          value={view.q}
          placeholder="search type, entity, actor, reason, changed keys"
          isSmallVariant
          onChange={(searchEvent) => onChange({ q: searchEvent.target.value })}
        />
        <button type="button" className="ce-chip" aria-pressed={open} onClick={() => setManuallyOpen(!open)}>
          filters
          {!!active && <i>{active}</i>}
        </button>
        <button type="button" className="ce-chip" onClick={() => onChange({ sort: view.sort === "desc" ? "asc" : "desc" })}>
          {view.sort === "desc" ? "newest first" : "oldest first"}
        </button>
        {canReset && (
          <button type="button" className="ce-linkish" onClick={onReset}>
            reset
          </button>
        )}
      </div>

      {open && (
        <div className="ce-panel">
          <div className="ce-row">
            <span className="ce-label">scope</span>
            {SCOPES.map((scope) => (
              <FilterChip
                label={scope}
                count={counts.byScope[scope]}
                pressed={view.scope.indexOf(scope) !== -1}
                key={scope}
                onToggle={(solo) => onChange({ scope: toggleValue<ChangeEventTimelineScope>(view.scope, SCOPES, scope, solo) })}
              />
            ))}
          </div>

          <div className="ce-row">
            <span className="ce-label">family</span>
            {FAMILIES.map((family) => (
              <FilterChip
                label={family}
                count={counts.byFamily[family]}
                pressed={view.fam.indexOf(family) !== -1}
                family={family}
                key={family}
                onToggle={(solo) => onChange({ fam: toggleValue<ChangeEventFamily>(view.fam, FAMILIES, family, solo) })}
              />
            ))}
          </div>

          <div className="ce-row">
            <span className="ce-label">rows</span>
            <button type="button" className="ce-chip" aria-pressed={view.baseline} onClick={() => onChange({ baseline: !view.baseline })}>
              baseline
              <i>{counts.baseline}</i>
            </button>
            <Form className="ce-range" name="changeEventRange">
              <RangePicker
                name="range"
                label="date range"
                value={view.from && view.to ? [moment(view.from), moment(view.to)] : undefined}
                onChange={(dates) => onChange({ from: dates[0]?.format("YYYY-MM-DD") ?? "", to: dates[1]?.format("YYYY-MM-DD") ?? "" })}
              />
            </Form>
            {(view.from || view.to) && (
              <button type="button" className="ce-linkish" onClick={() => onChange({ from: "", to: "" })}>
                clear dates
              </button>
            )}
            <span className="ce-hint">shift-click a chip for only that one</span>
          </div>
        </div>
      )}
    </div>
  );
};
